import React, { FC, useEffect, useMemo, useState } from "react";
import { BackHandler, Keyboard, KeyboardAvoidingView, Platform, ScrollView, useWindowDimensions, View } from "react-native";
import * as Yup from 'yup'
import { useFormik } from "formik";
import Popover from "react-native-popover-view/dist/Popover";
import { useIsFocused } from "@react-navigation/native";
import { CountryProps, StateValueProps, TimeZoneDataProps, timeZoneData } from "./locationModule";

import { config } from "../../../shared/environment";
import { getScreenPermission, useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { appColors, appFonts, fontSize } from "../../../themes/appTheme";
import { checkDuplicatesRedux, getCountry } from "../../../redux/slices/commonSlice";
import CommonHeader from "../../../shared/commonComponents/commonHeader";
import CommonDialogBox from "../../../shared/commonComponents/commonDialogBox";
import { LocationStyle } from "../../../themes/components/location/locationStyle";
import { ZenButton, ZenCheckBox, ZenDivider, ZenDropdown, ZenInput, ZenLoader, ZenSwitch, ZenText, ZenToast } from "zen-core-elements";
import { addPosLocation, editPosLocation, getOnePosLocationDetails, getPosStateValues, setCountryName } from "../../../redux/slices/locationSlice";
import { useAppNavigation, useAppRoute } from "../../../types/@react-navigation/navigation";
import { PayXGetFreeStyle } from "../../../themes/components/discounts/payXGetFreeStyle";
import { shallowEqual } from "react-redux";

const AddEditLocation: FC = () => {
    const { isWrite: isSettingWrite } = useAppSelector(getScreenPermission("SettingStack"), shallowEqual)
    //variable which holds route params
    const { params } = useAppRoute("AddEditLocation")
    //variable to store the screen width
    const { width, height } = useWindowDimensions()
    const isLandScape = useMemo(() => width > height, [width, height])
    //variable to open/close country dropdown
    const [countryOpen, setCountryOpen] = useState<boolean>(false)
    //variable to open/close time zone dropdown
    const [timeZoneOpen, setTimeZoneOpen] = useState<boolean>(false)
    //variable to open/close state dropdown 
    const [stateOpen, setStateOpen] = useState<boolean>(false)
    //variable to store navigation from useNavigation hook
    const navigation = useAppNavigation()
    //variable to store time zone dropdown items
    const [timeZoneValue, setTimeZoneValue] = useState<any>([])
    //variable to store the focused state from useIsFocused hook
    const isFocused = useIsFocused()
    //variable to store the loading status
    const [load, setLoad] = useState<boolean>(false)
    //variable to open/close page leave confirmation popover
    const [leavePopover, setLeavePopover] = useState<boolean>(false)
    //used to dispatch actions
    const dispatch = useAppDispatch()
    //variable used to track form dirty
    const [isDirty, setIsDirty] = useState<boolean>(false)
    //variable used to hold popover type
    const [popoverType, setPopoverType] = useState<string>('')
    const [isLimitExceeded, setIsLimitExceeded] = useState(false)

    //variable to fetch cached countries
    const getCountryRes = useAppSelector(state => state.common.getCountry?.value);
    const isDublicate = useAppSelector(state => state.common.checkDuplicate?.value?.result?.alreadyExists)
    const locationData = useAppSelector(state => state.location.getOneLocation?.value?.location)
    const countryData = useAppSelector(state => state.common.countriesDropdownOptions)
    const stateData = useAppSelector(state => state.location.statesDropdownOptions)
    const currentCountry = useAppSelector(state => state.location.countryName)

    //variable to store the defaultWarning confirmation dialog
    const defaultWarningProps = {
        content: "There must be at least one default location. Please select any other store location as the default location!",
        button: [{ id: 1, name: "okay" }],
        icon: { name: 'alert-outline', size: 55, color: appColors.warning, type: 'material-community' }
    }
    //variable to store the limit exceeded confirmation dialog
    const limitExceededProps = {
        content: 'Your Location limit has been exceeded!. Please upgrade your plan to add more Locations.',
        button: [{ id: 1, name: "okay" }],
        icon: { name: 'alert-outline', size: 55, color: appColors.warning, type: 'material-community' }
    }

    //variable to store the statusWarning confirmation dialog
    const statusWarningProps = {
        content: "The selected location is the default location. Please set another store location as the default location to disable this location.",
        button: [{ id: 1, name: "okay" }],
        icon: { name: 'alert-outline', size: 55, color: appColors.warning, type: 'material-community' }
    }

    //variable to store the alertDefault confirmation dialog
    const alertDefaultChangeProps = {
        content: "The default address already exists! Do you want to make this your Default Address?",
        button: [{ id: 1, name: "yes" }, { id: 2, name: 'no' }],
        icon: { name: 'alert-outline', size: 55, color: appColors.warning, type: 'material-community' }
    }

    const commonConfirmation = (event: { id: number }) => {
        if (event) {
            setPopoverType('');
            navigation?.goBack();
        }
    }

    //yup validations
    const validationSchema = Yup.object().shape({
        locationName: Yup.string().
            label('locationName').
            max(60, 'Location name should not exceed 60 characters').
            required('Location name is required'),
        addrLine1: Yup.string().label('addrLine1').
            label('addrLine1').
            max(100, 'Address should not exceed 100 characters').
            required('Address is required'),
        addrLine2: Yup.string().label('addrLine2').
            label('addrLine2')
            .nullable()
            .max(100, 'Address should not exceed 100 characters'),
        timeZone: Yup.string().label('timeZone').required('Time zone is required'),
        country: Yup.number().label('country').required('Country is required'),
        state: Yup.number().label('state').required('State is required'),
        city: Yup.string().label('city').required('City is required'),
        zipCode: Yup.string().label('zipCode').required('ZipCode is required'),
        email: Yup.string().label('email').nullable().email('Invalid email'),
        countryCode: Yup.string(),
        contactNumber: Yup.string().label('contactNumber').nullable().matches(/^[0-9]+$/, 'Invalid contact number'),
        latitude: Yup.number().label('latitude').max(90, 'Latitude should not exceed 90').min(-90, 'Latitude should be greater than -90').nullable().integer('Latitude accepts only whole numbers.').typeError('Latitude accepts only whole numbers.'),
        longitude: Yup.number().label('longitude').nullable().min(-180, 'Longitude should be greater than -180').max(180, 'Longitude should not exceed 180').integer('Longitude accepts only whole numbers').typeError('Longitude accepts only whole numbers.'),
        gstCode: Yup.number().nullable().max(15, 'GST number should not exceed 15 characters')
    })

    const formik = useFormik({
        initialValues: {
            locationName: params?.editId ? locationData?.locationName! : '',
            addrLine1: params?.editId ? locationData?.addresses?.addressLine1 : '',
            addrLine2: params?.editId ? locationData?.addresses?.addressLine2! : '',
            timeZone: params?.editId ? timeZoneData?.timeZones?.find((i: TimeZoneDataProps) => i?.value == locationData?.timeZone)?.label : '',
            country: params?.editId ? countryData?.find((e) => e?.countryCode == locationData?.addresses?.countryCode)?.id : '',
            state: params?.editId ? locationData?.addresses?.state : '',
            city: params?.editId ? locationData?.addresses?.city : '',
            zipCode: params?.editId ? locationData?.addresses?.zipCode : '',
            email: params?.editId ? locationData?.addresses?.email! : '',
            countryCode: params?.editId ? locationData?.addresses?.countryCode! : '',
            contactNumber: params?.editId ? locationData?.addresses?.mobileNumber! : '',
            latitude: params?.editId ? locationData?.latitude! : '',
            longitude: params?.editId ? locationData?.longitude : '',
            squarePosUuid: params?.editId ? locationData?.squarePosUuid! : '',
            isActive: params?.editId ? locationData?.isActive == true ? true : false : true,
            isDefault: params?.editId ? locationData?.isDefault! : false,
            inventoryAvailable: params?.editId ? locationData?.isWeb! : false
        },
        enableReinitialize: true,
        onSubmit: (values) => { saveLocationDetails(values) },
        validationSchema: validationSchema
    })

    //useEffect which gets called when page focuses
    useEffect(() => {
        if (isFocused) {
            setLoad(true);
            getTimeZone();
            if (params?.editId) {
                getOneLocationDetail();
            } else {
                getPosCountry();
            }
        }
    }, [isFocused, params?.editId])

    //UseEffect function which is used to perform backhandler operation
    useEffect(() => {
        BackHandler.addEventListener('hardwareBackPress', function () {
            if (formik?.dirty) {
                setLeavePopover(true)
            }
            else {
                navigation.goBack()
            }
            return true
        })
    }, [formik?.dirty])

    //function used to handle leave confirmation popover
    const confirmation = (item: { id: number }) => {
        if (item?.id == 2) {
            setLeavePopover(false)
            navigation.goBack()
        } else {
            setLeavePopover(false)
        }
    }
    //function used to handle default change confirmation popover
    const defaultChangeConfirmation = (item: { name: string }) => {
        if (item?.name == "yes") {
            if (popoverType == "alertDefaultChange") {
                formik?.setFieldValue("isActive", true);
                formik?.setFieldValue("isDefault", true);
            }
            setPopoverType('');
        } else {
            setPopoverType('');
            formik?.setFieldValue("isDefault", false);
        }
    }

    //useEffect to check duplication of categoryName
    useEffect(() => {
        if (formik?.values?.locationName != '' && !params?.editId) {
            const duplicateDebounceFunction = setTimeout(async () => {
                const payload = {
                    storeId: config?.storeId,
                    data: {
                        "value": (formik?.values?.locationName?.trim()).toLowerCase(),
                        "tableName": "inventoryLocations", "columnName": "locationName",
                        "id": params?.editId!,
                        "storeId": true
                    }
                }
                await dispatch(checkDuplicatesRedux(payload)).unwrap()
                    .catch((err) => { console.log('Err in checkDuplicate', err) })
            }, 500)
            return () => clearTimeout(duplicateDebounceFunction)
        }
    }, [formik?.values?.locationName])

    //function used to add/update location details
    const saveLocationDetails = (values: typeof formik.values) => {
        closeDropdown()
        setLoad(true);
        let countryArr: CountryProps[] = []
        let stateArr: Pick<StateValueProps, "id">[] = []
        countryArr = countryData
            ?.filter(i => i?.value == values?.country)
            ?.map(({ id, countryCode }) => ({ id, countryCode }));
        stateArr = stateData?.[currentCountry!]?.filter((i) => i?.value == values?.state)?.map(({ id }) => ({ id })) ?? [];
        let tZ = timeZoneData.timeZones.find((i: TimeZoneDataProps) => i?.label == values?.timeZone)?.value
        // Without these the PUT fails validation, or updates by a null/stale address id while still reporting success
        if (!countryArr?.[0] || !stateArr?.[0] || (params?.editId && !locationData?.addressId)) {
            setLoad(false);
            ZenToast({ type: 'error', message: 'Unable to save location. Please reload the location details and try again.' });
            return;
        }

        let props: any = {
            storeId: config?.storeId,
            data: {
                id: locationData?.addressId ? locationData?.addressId : null,
                addressLine1: values?.addrLine1 ? values?.addrLine1.trim() : null,
                addressLine2: values?.addrLine2 ? values?.addrLine2.trim() : null,
                branchName: values?.locationName?.trim(),
                city: values?.city?.trim(),
                contactNumber: values?.contactNumber ? values?.contactNumber?.trim() : null,
                country: countryArr[0],
                countryCode: values?.countryCode ? values?.countryCode : '',
                email: values?.email ? values?.email?.trim() : null,
                isActive: values?.isActive,
                isDefault: values?.isDefault,
                latitude: values?.latitude ? values?.latitude?.trim() : null,
                longitude: values?.longitude ? values?.longitude?.trim() : null,
                state: stateArr[0],
                timeZone: values.timeZone ? tZ : null,
                zipCode: values?.zipCode ? values?.zipCode?.trim() : null,
                isPos: true,
                isWeb: values?.inventoryAvailable,
                createdFrom: 'POS',
                squarePosUuid: values?.squarePosUuid
            }
        }
        if (params?.editId) {
            props.id = params?.editId;
        }
        if (!params?.editId) {
            dispatch(addPosLocation(props))?.unwrap()?.then((addLocResponse) => {
                if (addLocResponse?.storeLocation?.limitExceededMessage) {
                    setIsLimitExceeded(true)
                    setLoad(false);
                }
                else {
                    setLoad(false);
                    navigation?.goBack();
                    setTimeout(() => { ZenToast({ type: 'success', message: 'Location created successfully' }) }, 50)
                }
            }).catch((err) => {
                setLoad(false)
                console.log(err?.message, "Error in add Pos location");

            })
        } else {
            dispatch(editPosLocation(props))?.unwrap()?.then((editLocResponse) => {
                if (editLocResponse) {
                    setLoad(false);
                    navigation?.goBack();
                    setTimeout(() => { ZenToast({ type: 'success', message: 'Location updated successfully' }) }, 50)
                }
            }).catch((err) => {
                setLoad(false)
                console.log(err?.message, "Error in update Pos location");

            })
        }
    }
    //function used to get zone 
    const getTimeZone = () => {
        const timeZoneval = timeZoneData?.timeZones?.length > 0 ? timeZoneData?.timeZones?.map(item => ({ label: item.label, value: item.label })) : [];
        setTimeZoneValue(timeZoneval)
    }


    //function used to get countries
    const getPosCountry = (val?: number) => {
        if (getCountryRes?.country && getCountryRes.country.length > 0) {
            !params?.editId && setLoad(false);
            return;
        }
        dispatch(getCountry())?.unwrap()?.then(() => {
            !params?.editId && setLoad(false)
        })
    }

    //function used to get states
    const getStateValues = (name: string) => {
        if (!name) return;
        // keep countryName in sync even when the states come from cache (otherwise Save looks up another country's states)
        dispatch(setCountryName(name))
        if (stateData?.[name]?.length > 0) {
            setLoad(false);
            return;
        }
        dispatch(getPosStateValues(name))
    }

    const closeDropdown = (currentDropown?: "state" | "country" | "timeZone") => {
        if (currentDropown == 'state') {
            setCountryOpen(false)
            setTimeZoneOpen(false)
        }
        else if (currentDropown == 'country') {
            setTimeZoneOpen(false)
            setStateOpen(false)
        }
        else if (currentDropown == 'timeZone') {
            setCountryOpen(false)
            setStateOpen(false)
        }
        else {
            setStateOpen(false)
            setCountryOpen(false)
            setTimeZoneOpen(false)
        }
    }

    //function used to get single location details
    const getOneLocationDetail = () => {
        let props = {
            storeId: config?.storeId,
            id: params?.editId
        }
        dispatch(getOnePosLocationDetails(props))?.unwrap()?.then((getOneRes) => {
            if (getOneRes) {
                getPosCountry(getOneRes?.location?.addresses?.countryCode)
                const country = countryData?.find((i) => i?.countryCode == getOneRes?.location?.addresses?.countryCode)?.label
                getStateValues(country!)
            }
        }).catch((err) => {
            console.log('Error in getLocation details', err)
        }).finally(() => {
            setTimeout(() => { setLoad(false) }, 2000)
        })
    }

    return (
        <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={{ flex: 1, backgroundColor: appColors.lightBackgroundColor }} enabled onStartShouldSetResponder={() => { closeDropdown(); return false }} >
            <CommonHeader headerName={params?.editId ? `${isSettingWrite ? "Edit" : "View"} Location` : "Add Location"} backIcon backIconOnpress={() => { formik?.dirty ? setLeavePopover(true) : navigation.goBack() }} />
            <View style={[LocationStyle.flex]}>

                {isSettingWrite && <ZenButton
                    title="Save"
                    disabledStyle={{ borderColor: 'transparent' }}
                    onPress={() => { closeDropdown(); formik?.handleSubmit() }}
                    buttonStyle={[LocationStyle.formSaveButton, { width: 100, height: 40 }]}
                    titleStyle={[LocationStyle.formButtonTitle, { color: appColors?.light, top: -2 }]}
                    containerStyle={{ paddingVertical: 10, alignSelf: "flex-end", marginHorizontal: 15 }}
                />}
                {
                    load ? <View style={LocationStyle.loader}>
                        <ZenLoader viewStyle={{ backgroundColor: "transparent" }} color={appColors?.primary} lottieType="Local" lottie={require('../../../assets/infinityLoader.json')} />
                    </View> : <ScrollView showsVerticalScrollIndicator={false}>
                        <View style={{ borderRadius: 16, backgroundColor: appColors?.light, marginTop: 10, marginBottom: 10, padding: 18, marginHorizontal: 16, borderColor: appColors.transparent, elevation: 0 }}>
                            <View style={{ flexDirection: isLandScape ? 'row' : 'column', justifyContent: isLandScape ? 'space-between' : 'flex-start', alignItems: isLandScape ? 'center' : 'flex-start', paddingHorizontal: 4, gap: 12 }}>
                                <ZenText content={'Store Address'} color={appColors.primary} bold={true} size={fontSize.mediumFontSize} />
                                <View style={{ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 16 }}>
                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                                        <ZenText content={'Enable this location'} color={appColors.lightDark} style={{ fontSize: fontSize?.smallFontSize, fontFamily: appFonts.medium }} />
                                        <ZenSwitch
                                            value={formik?.values?.isActive}
                                            activeColor={appColors?.primary}
                                            activeThumbColor={appColors?.light}
                                            disabled={!isSettingWrite}
                                            inActiveColor={appColors?.borderColor}
                                            inactiveThumbColor={appColors?.light}
                                            onValueChange={() => {
                                                closeDropdown()
                                                if (formik?.values?.isDefault) {
                                                    setPopoverType("isChangeStatus");
                                                } else {
                                                    formik?.setFieldValue('isActive', !formik?.values?.isActive);
                                                }
                                                setIsDirty(true);
                                            }}
                                            style={{ transform: [{ scaleX: 0.8 }, { scaleY: 0.8 }] }}
                                        />
                                    </View>
                                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                                        <ZenText content={'Mark as default'} color={appColors.lightDark} size={fontSize?.smallFontSize} />
                                        <ZenSwitch
                                            activeColor={appColors?.primary}
                                            disabled={!isSettingWrite}
                                            activeThumbColor={appColors?.light}
                                            inActiveColor={appColors?.borderColor}
                                            inactiveThumbColor={appColors?.light} onValueChange={(val) => {
                                                closeDropdown()
                                                //setting another loc as default loc
                                                if (val && params?.locationArrLength && params?.locationArrLength > 0) {
                                                    setPopoverType("alertDefaultChange")
                                                }
                                                else if (val && params?.defaultLen == 0) {//while no default loc exist
                                                    formik.setFieldValue('isDefault', true)
                                                    formik.setFieldValue('isActive', true)
                                                } else {
                                                    //while disabling existing default location
                                                    if (locationData?.isDefault && params?.locationArrLength && params?.locationArrLength > 1) {
                                                        setPopoverType('isChangeDefault')
                                                    } else {
                                                        if (params?.locationArrLength == 1 && params?.defaultLen && params?.defaultLen < 1) {
                                                            ZenToast({
                                                                type: "info",
                                                                message: "Atleast one default location is required!"
                                                            })
                                                        }
                                                        else {
                                                            formik.setFieldValue('isDefault', false)
                                                        }
                                                    }
                                                }
                                                setIsDirty(true);
                                            }}
                                            value={formik?.values?.isDefault}
                                            style={{ transform: [{ scaleX: 0.8 }, { scaleY: 0.8 }] }}
                                        />
                                    </View>
                                </View>
                            </View>
                            <ZenDivider style={{ marginVertical: 15 }} />

                            <View style={{ flexDirection: isLandScape ? 'row' : 'column', justifyContent: 'space-between', gap: 15 }}>
                                <View style={{ flex: isLandScape ? 0.5 : undefined }}>
                                    <ZenInput
                                        type="outlined"
                                        onPress={() => closeDropdown()}
                                        disabled={!isSettingWrite}
                                        inputContainerStyle={{ paddingHorizontal: 8 }}
                                        disabledInputStyle={PayXGetFreeStyle.disableStyle}
                                        labelStyle={PayXGetFreeStyle.labelStyle}
                                        errorStyle={PayXGetFreeStyle.errorStyle}
                                        label="Location Name *"
                                        onChangeText={(text: string) => { formik?.setFieldValue('locationName', text) }}
                                        onBlur={() => { formik?.setFieldTouched('locationName', true); formik?.setFieldValue('locationName', formik?.values?.locationName) }}
                                        value={formik?.values?.locationName}
                                        errorMessage={formik?.touched?.locationName && formik?.errors?.locationName ? formik?.touched?.locationName && formik?.errors?.locationName : isDublicate ? 'Location name already exists' : undefined}
                                    />
                                </View>
                                <View style={{ flex: isLandScape ? 0.5 : undefined }}>
                                    <ZenInput
                                        type="outlined"
                                        label="Address line1 *"
                                        disabled={!isSettingWrite}
                                        onPress={() => closeDropdown()}
                                        inputContainerStyle={{ paddingHorizontal: 8 }}
                                        disabledInputStyle={PayXGetFreeStyle.disableStyle}
                                        labelStyle={PayXGetFreeStyle.labelStyle}
                                        errorStyle={PayXGetFreeStyle.errorStyle}
                                        onChangeText={(text: string) => { formik?.setFieldValue('addrLine1', text) }}
                                        onBlur={() => { formik?.setFieldTouched('addrLine1', true); formik?.setFieldValue('addrLine1', formik?.values?.addrLine1) }}
                                        value={formik?.values?.addrLine1}
                                        errorMessage={formik?.touched?.addrLine1 && formik?.errors?.addrLine1 ? formik?.touched?.addrLine1 && formik?.errors?.addrLine1 : undefined}
                                    />
                                </View>
                            </View>
                            <View style={{ flexDirection: isLandScape ? 'row' : 'column', justifyContent: 'space-between', gap: 15 }}>
                                <View style={{ flex: isLandScape ? 0.5 : undefined }}>
                                    <ZenInput
                                        type="outlined"
                                        label="Address line2"
                                        disabled={!isSettingWrite}
                                        onPress={() => closeDropdown()}
                                        inputContainerStyle={{ paddingHorizontal: 8 }}
                                        disabledInputStyle={PayXGetFreeStyle.disableStyle}
                                        labelStyle={PayXGetFreeStyle.labelStyle}
                                        errorStyle={PayXGetFreeStyle.errorStyle}
                                        onChangeText={(text: string) => { formik?.setFieldValue('addrLine2', text) }}
                                        value={formik?.values?.addrLine2}
                                    />
                                </View>
                                <View style={{ zIndex: Platform.OS == 'ios' ? 1 : undefined, flex: isLandScape ? 0.5 : undefined }}>
                                    <ZenDropdown
                                        title="TimeZone *"
                                        disabled={!isSettingWrite}
                                        placeholder=""
                                        titleStyle={{ color: appColors.labelColor, fontSize: fontSize.smallFontSize }}
                                        items={timeZoneValue}
                                        value={formik?.values?.timeZone ?? null}
                                        multiple={false}
                                        open={timeZoneOpen}
                                        listItemLabelStyle={{ fontSize: fontSize?.smallFontSize }}
                                        labelStyle={{ fontSize: fontSize?.smallFontSize, color: appColors?.dark, fontFamily: appFonts?.medium }}
                                        onOpen={() => Keyboard?.dismiss()}
                                        onClose={() => Keyboard?.dismiss()}
                                        onPress={() => closeDropdown("timeZone")}
                                        setOpen={setTimeZoneOpen}
                                        autoScroll={true}
                                        searchable={true}
                                        searchPlaceholder="Search Timezone"
                                        maxHeight={160}
                                        dropdownDirection="TOP"
                                        setValue={() => { }}
                                        onSelectItem={(val) => { formik?.setFieldValue('timeZone', val?.value) }}
                                        style={{ height: 45 }}
                                    />
                                </View>
                            </View>
                            <View style={{ flexDirection: isLandScape ? 'row' : 'column', zIndex: Platform.OS == 'ios' ? 1 : undefined, justifyContent: 'space-between', gap: 15, marginBottom: 12 }}>
                                <View style={{ flex: isLandScape ? 0.5 : undefined }}>
                                    <ZenDropdown
                                        disabled={!isSettingWrite}
                                        items={countryData}
                                        title="Country *"
                                        titleStyle={{ color: appColors.labelColor, fontSize: fontSize.smallFontSize }}
                                        setValue={() => { }}
                                        placeholder=""
                                        multiple={false}
                                        value={Number(formik?.values?.country)}
                                        onSelectItem={(item) => {
                                            dispatch(setCountryName(item?.label))
                                            formik?.setFieldValue('country', item?.value);
                                            formik?.setFieldValue('countryCode', item?.countryCode);
                                            getStateValues(item?.label)
                                            formik?.setFieldValue('state', null);
                                        }}
                                        listItemLabelStyle={{ fontSize: fontSize?.smallFontSize }}
                                        labelStyle={{ fontSize: fontSize?.smallFontSize, color: appColors?.dark, fontFamily: appFonts?.medium }}
                                        onOpen={() => Keyboard?.dismiss()}
                                        onClose={() => Keyboard?.dismiss()}
                                        searchable={true}
                                        searchPlaceholder="Search Country"
                                        open={countryOpen}
                                        setOpen={setCountryOpen}
                                        style={{ height: 45 }}
                                        onPress={() => { formik?.setFieldTouched('country', true); closeDropdown("country") }}
                                    />
                                    {formik?.touched?.country && formik?.values?.country == undefined ? <ZenText content="Country is required" color={appColors.red} style={{ fontSize: fontSize?.errorFontSize, fontFamily: appFonts.medium }} /> : null}
                                </View>

                                <View style={{ zIndex: Platform.OS == 'ios' ? 1 : undefined, flex: isLandScape ? 0.5 : undefined }}>
                                    <ZenDropdown
                                        items={stateData[currentCountry!]}
                                        title="State *"
                                        disabled={!isSettingWrite}
                                        placeholder=""
                                        titleStyle={{ color: appColors.labelColor, fontSize: fontSize.smallFontSize }}
                                        setValue={() => { }}
                                        multiple={false}
                                        listItemLabelStyle={{ fontSize: fontSize?.smallFontSize }}
                                        labelStyle={{ fontSize: fontSize?.smallFontSize, color: appColors?.dark, fontFamily: appFonts?.medium }}
                                        onOpen={() => Keyboard?.dismiss()}
                                        onClose={() => Keyboard?.dismiss()}
                                        value={Number(formik?.values?.state)}
                                        onSelectItem={(item) => { formik?.setFieldValue('state', item?.value) }}
                                        onPress={() => { formik?.setFieldTouched('state', true); closeDropdown("state") }}
                                        autoScroll={true}
                                        searchable={true}
                                        searchPlaceholder="Search State"
                                        open={stateOpen}
                                        setOpen={setStateOpen}
                                        dropDownContainerStyle={{ borderColor: appColors.lightGrey }}
                                        style={[{ height: 45 }]}
                                    />
                                    {formik?.touched?.state && formik?.values?.state == '' ? <ZenText content="State is required" color={appColors.red} style={{ fontSize: fontSize?.errorFontSize, fontFamily: appFonts.medium }} /> : null}
                                </View>
                            </View>
                            <View style={{ flexDirection: isLandScape ? 'row' : 'column', justifyContent: 'space-between', gap: 15 }}>
                                <View style={{ flex: isLandScape ? 0.5 : undefined }}>
                                    <ZenInput
                                        type="outlined"
                                        label="City *"
                                        disabled={!isSettingWrite}
                                        onPress={() => closeDropdown()}
                                        inputContainerStyle={{ paddingHorizontal: 8 }}
                                        disabledInputStyle={PayXGetFreeStyle.disableStyle}
                                        labelStyle={PayXGetFreeStyle.labelStyle}
                                        errorStyle={PayXGetFreeStyle.errorStyle}
                                        value={formik?.values?.city}
                                        onChangeText={(text: string) => formik?.setFieldValue('city', text)}
                                    />
                                </View>
                                <View style={{ flex: isLandScape ? 0.5 : undefined }}>
                                    <ZenInput
                                        type="outlined"
                                        label="Zipcode *"
                                        disabled={!isSettingWrite}
                                        onPress={() => closeDropdown()}
                                        inputContainerStyle={{ paddingHorizontal: 8 }}
                                        disabledInputStyle={PayXGetFreeStyle.disableStyle}
                                        labelStyle={PayXGetFreeStyle.labelStyle}
                                        errorStyle={PayXGetFreeStyle.errorStyle}
                                        value={formik?.values?.zipCode}
                                        onChangeText={(text: string) => formik?.setFieldValue('zipCode', text)}
                                    />
                                </View>
                            </View>
                            <View style={{ flexDirection: isLandScape ? 'row' : 'column', justifyContent: 'space-between', gap: 15 }}>
                                <View style={{ flex: isLandScape ? 0.5 : undefined }}>
                                    <ZenInput
                                        type="outlined"
                                        label="Email"
                                        keyboardType="email-address"
                                        disabled={!isSettingWrite}
                                        onPress={() => closeDropdown()}
                                        value={formik?.values?.email}
                                        inputContainerStyle={{ paddingHorizontal: 8 }}
                                        disabledInputStyle={PayXGetFreeStyle.disableStyle}
                                        labelStyle={PayXGetFreeStyle.labelStyle}
                                        errorStyle={PayXGetFreeStyle.errorStyle}
                                        onChangeText={(text: string) => { formik?.setFieldValue('email', text) }} />
                                </View>
                                <View style={{ flex: isLandScape ? 0.5 : undefined }}>
                                    <ZenText content="Contact Number" color={appColors.labelColor} style={{ fontSize: fontSize?.smallFontSize, marginLeft: 9 }} />
                                    <View style={{ flexDirection: 'row', alignItems: 'center', width: '100%' }}>
                                        <View style={{ width: 60 }}>
                                            <ZenInput
                                                type="outlined"
                                                editable={false}
                                                disabled={!isSettingWrite}
                                                onPress={() => closeDropdown()}
                                                inputContainerStyle={{ paddingHorizontal: 8, borderRightWidth: 0, borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                                                disabledInputStyle={PayXGetFreeStyle.disableStyle}
                                                labelStyle={PayXGetFreeStyle.labelStyle}
                                                errorStyle={PayXGetFreeStyle.errorStyle}
                                                value={formik?.values?.countryCode ? '+' + formik?.values?.countryCode?.toString() : ''}
                                                onChangeText={(text: string) => formik?.setFieldValue('countryCode', text)}
                                            />
                                        </View>
                                        <View style={{ flex: 1 }}>
                                            <ZenInput
                                                type="outlined"
                                                keyboardType="numeric"
                                                disabled={!isSettingWrite}
                                                onPress={() => closeDropdown()}
                                                value={formik?.values?.contactNumber}
                                                inputContainerStyle={{ paddingHorizontal: 8, borderTopLeftRadius: 0, borderBottomLeftRadius: 0, right: 3 }}
                                                disabledInputStyle={PayXGetFreeStyle.disableStyle}
                                                labelStyle={PayXGetFreeStyle.labelStyle}
                                                errorStyle={PayXGetFreeStyle.errorStyle}
                                                onBlur={() => { formik.setFieldTouched('contactNumber', true); formik.setFieldValue("contactNumber", formik?.values?.contactNumber?.trim()) }}
                                                onChangeText={(text: string) => formik?.setFieldValue('contactNumber', text)}
                                                errorMessage={formik?.touched?.contactNumber && formik?.errors?.contactNumber ? formik?.touched?.contactNumber && formik?.errors?.contactNumber : undefined}
                                            />
                                        </View>
                                    </View>
                                </View>
                            </View>
                            <View style={{ flexDirection: isLandScape ? 'row' : 'column', justifyContent: 'space-between', gap: 15 }}>
                                <View style={{ flex: isLandScape ? 0.5 : undefined }}>
                                    <ZenInput
                                        type="outlined"
                                        label="Latitude"
                                        disabled={!isSettingWrite}
                                        value={formik?.values?.latitude}
                                        onPress={() => closeDropdown()}
                                        inputContainerStyle={{ paddingHorizontal: 8 }}
                                        disabledInputStyle={PayXGetFreeStyle.disableStyle}
                                        labelStyle={PayXGetFreeStyle.labelStyle}
                                        errorStyle={PayXGetFreeStyle.errorStyle}
                                        onChangeText={(text: string) => { formik.setFieldValue('latitude', text) }}
                                        onBlur={() => { formik.setFieldTouched('latitude', true); formik.setFieldValue('latitude', formik?.values?.latitude?.trim()) }}
                                        keyboardType="numeric"
                                        errorMessage={formik?.touched?.latitude && formik?.errors?.latitude ? formik?.touched?.latitude && formik?.errors?.latitude : undefined}
                                    />
                                </View>
                                <View style={{ flex: isLandScape ? 0.5 : undefined }}>
                                    <ZenInput
                                        type="outlined"
                                        onPress={() => closeDropdown()}
                                        disabled={!isSettingWrite}
                                        inputContainerStyle={{ paddingHorizontal: 8 }}
                                        disabledInputStyle={PayXGetFreeStyle.disableStyle}
                                        labelStyle={PayXGetFreeStyle.labelStyle}
                                        errorStyle={PayXGetFreeStyle.errorStyle}
                                        value={formik?.values?.longitude!}
                                        label="Longitude"
                                        onChangeText={(text: string) => formik?.setFieldValue('longitude', text)}
                                        keyboardType="numeric"
                                        onBlur={() => { formik.setFieldTouched('longitude', true); formik.setFieldValue('longitude', formik?.values?.longitude?.trim()) }}
                                        errorMessage={formik?.touched?.longitude && formik?.errors?.longitude ? formik?.touched?.longitude && formik?.errors?.longitude : undefined}
                                    />
                                </View>
                            </View>
                            <View style={{ flex: isLandScape ? 0.5 : undefined }}>
                                <ZenInput
                                    type="outlined"
                                    onPress={() => closeDropdown()}
                                    inputContainerStyle={{ paddingHorizontal: 8 }}
                                    disabled={!isSettingWrite}
                                    value={formik?.values?.squarePosUuid}
                                    label="Square Pos Location id"
                                    keyboardType="numeric"
                                    labelStyle={{ fontSize: fontSize.smallFontSize, color: appColors.labelColor }}
                                    onChangeText={(text: string) => { formik?.setFieldValue('squarePosUuid', text) }}
                                    onBlur={() => { formik.setFieldTouched('squarePosUuid', true); formik.setFieldValue('squarePosUuid', formik?.values?.squarePosUuid?.trim()) }}
                                />
                            </View>
                        </View>
                        <View style={{ borderRadius: 16, marginBottom: 20, flex: 1, elevation: 0, borderColor: appColors.transparent, marginHorizontal: 16, padding: 15, backgroundColor: appColors?.light }}>
                            <ZenText content={'Order Setup'} color={appColors.primary} bold={true} size={fontSize.mediumFontSize} style={{ alignSelf: 'flex-start' }} />
                            <ZenDivider style={{ marginVertical: 15 }} />
                            <View style={{ marginBottom: 10 }}>
                                <ZenText content={'Fulfill online orders'} color={appColors.lightDark} size={fontSize?.smallFontSize} />
                                <ZenCheckBox
                                    titleStyle={{ fontSize: fontSize.smallFontSize, color: appColors.lightDark, marginLeft: 2, fontFamily: appFonts.medium }}
                                    title={'Inventory at this location is available for online sales'}
                                    disabled={!isSettingWrite}
                                    checked={formik?.values?.inventoryAvailable}
                                    checkedColor={appColors.primary}
                                    containerStyle={{ marginTop: 8 }}
                                    onPress={() => { formik?.setFieldValue('inventoryAvailable', !formik?.values?.inventoryAvailable); closeDropdown() }} />
                            </View>
                        </View>
                        <Popover isVisible={leavePopover} popoverStyle={{ width: width > 900 ? 600 : isLandScape ? 450 : 350, borderRadius: 10 }}>
                            <CommonDialogBox type="EDIT" replaceValue="page" confirmation={confirmation} />
                        </Popover>

                        <Popover isVisible={popoverType == "isChangeDefault"} popoverStyle={{ width: width > 900 ? 600 : isLandScape ? 450 : 350, borderRadius: 10 }}>
                            <CommonDialogBox dialog={defaultWarningProps} type="OTHER" confirmation={commonConfirmation} />
                        </Popover>

                        <Popover isVisible={popoverType == "isChangeStatus"} popoverStyle={{ width: width > 900 ? 600 : isLandScape ? 450 : 350, borderRadius: 10 }}>
                            <CommonDialogBox dialog={statusWarningProps} type="OTHER" confirmation={commonConfirmation} />
                        </Popover>

                        <Popover isVisible={popoverType == "alertDefaultChange"} popoverStyle={{ width: width > 900 ? 600 : isLandScape ? 450 : 350, borderRadius: 10 }}>
                            <CommonDialogBox dialog={alertDefaultChangeProps} type="OTHER" confirmation={defaultChangeConfirmation} />
                        </Popover>

                        <Popover isVisible={isLimitExceeded} popoverStyle={{ width: width > 900 ? 600 : isLandScape ? 450 : 350, borderRadius: 10 }}>
                            <CommonDialogBox dialog={limitExceededProps} type="OTHER" confirmation={commonConfirmation} />
                        </Popover>

                    </ScrollView>}
            </View>
        </KeyboardAvoidingView>
    )
}
export default AddEditLocation
